import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import { Document, DocumentLinkInfo } from 'app/models/dto/document.model';
import {
  SubscribeToBorrowerDocuments,
  GetBorrowerDocuments,
  GetViewableMimetypes,
  ClearDocumentStore,
  UpdateDocument,
  UpdateDocumentDrsVerification,
  DeleteDocument,
  UploadDocument,
  GetDocumentCategories,
  UpdateDocumentAlert,
  RunExperianCommercial,
  SetDocumentContact,
  UploadAdvDocument
} from 'app/store/documents/document.actions';
import { DocumentsService } from 'app/services/documents/documents.service';
import {
  AddStackableNotificationError, AddStackableNotificationSuccess
} from 'ng2-app/store/stackable-notifications/StackableNotifications.actions';
import { tap, map, catchError } from 'rxjs/operators';
import { throwError, of } from 'rxjs';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { DocumentCategory } from 'app/models/dto/document-category.model';
import { environment } from 'environments/environment';
import { Injectable } from '@angular/core';
import { Alert } from 'app/models/dto/alert.model';

export class DocumentStateModel {
  borrowerId: null | number;
  loading: boolean;
  documents: Document[];
  documentCategories: DocumentCategory[];
  viewableMimetypes: string[];
  alert: Alert | null;
  isUploading: boolean;
}

@State<DocumentStateModel>({
  name: 'documents',
  defaults: {
    borrowerId: null,
    loading: true,
    documentCategories: [],
    documents: [],
    viewableMimetypes: [],
    alert: null,
    isUploading: false,
  }
})

@Injectable()

export class DocumentState {

  @Selector()
  static documents(state: DocumentStateModel) {
    return state.documents;
  }

  @Selector()
  static isUploading(state: DocumentStateModel) {
    return state.isUploading;
  }

  @Selector()
  static loading(state: DocumentStateModel) {
    return state.loading;
  }

  @Selector()
  static loadingOrUploading(state: DocumentStateModel) {
    return state.loading || state.isUploading;
  }

  @Selector()
  static alert(state: DocumentStateModel) {
    return state.alert;
  }

  @Selector()
  static documentCategories(state: DocumentStateModel) {
    return state.documentCategories;
  }

  @Selector()
  static viewableMimetypes(state: DocumentStateModel) {
    return state.viewableMimetypes;
  }


  @Selector()
  static dealDocumentCategories(state: DocumentStateModel) {
    return (dealDocumentTypes: any) =>
      state.documentCategories.length ? state.documentCategories.filter(cat => dealDocumentTypes[cat.id]) : [];
  }

  @Selector()
  static documentLinkInfo() {
    return () => {
      const documentLinkInfo = {
        baseUrl: environment.API_URL,
        queryParameters: '?stream=1'
      } as DocumentLinkInfo;
      return documentLinkInfo;
    };
  }

  constructor(
    private documentsService: DocumentsService,
    private pusherService: PusherService,
    private store: Store
  ) { }

  @Action(SubscribeToBorrowerDocuments, { cancelUncompleted: true })
  subscribeToBorrowerDocuments({ getState }: StateContext<DocumentStateModel>, { borrowerId }: SubscribeToBorrowerDocuments) {
    const prevBorrowerId = getState().borrowerId;
    if (prevBorrowerId === borrowerId) {
      return;
    } else if (prevBorrowerId) {
      this.pusherUnsubscribe(prevBorrowerId);
    }

    this.pusherSubscribe(borrowerId);
    return this.store.dispatch(new GetBorrowerDocuments(borrowerId));
  }

  @Action(UpdateDocumentAlert, { cancelUncompleted: true })
  updateDocumentAlert({ patchState }: StateContext<DocumentStateModel>, { alert }: UpdateDocumentAlert) {
    patchState({ alert: alert});
  }

  @Action(GetBorrowerDocuments, { cancelUncompleted: true })
  getBorrowerDocuments({ getState, patchState }: StateContext<DocumentStateModel>, { borrowerId, refresh }: GetBorrowerDocuments) {
    const prevBorrowerId = getState().borrowerId;

    if (borrowerId === prevBorrowerId && !refresh) {
      return;
    }

    if (borrowerId !== prevBorrowerId) {
      if (prevBorrowerId) {
        this.pusherUnsubscribe(prevBorrowerId);
      }

      // reset state
      patchState({
        borrowerId,
        documents: [],
        loading: true
      });
    } else {
      patchState({
        loading: true
      });
    }

    return this.documentsService.getBorrowerDocuments(borrowerId).pipe(catchError(() => {
      patchState({
        alert: {
          level: 'error',
          message: 'Failed to retrieve documents, please reload page to try again.'
        }
      });
      return of([]);
    }),tap((documents: Document[]) => {
      const filenames = documents.map(document => document.filename);
      documents.forEach(document => {
        document.isDupeFilename = (filenames.filter(filename => filename === document.filename).length > 1);
        document.ext = document.title.substr(document.title.lastIndexOf('.'));
      });
      patchState({
        documents,
        loading: false
      });
    }));
  }

  // TODO avoid unnecessary api calls in GetViewableMimetypes
  @Action(GetViewableMimetypes)
  getViewableMimetypes({ patchState }: StateContext<DocumentStateModel>) {
    return this.documentsService.getViewableMimetypes().pipe(tap(viewableMimetypes => {
      const extensions = Object.keys(viewableMimetypes);
      const mimetypes = Object.values(viewableMimetypes);
      viewableMimetypes = {extensions, mimetypes};
      patchState({ viewableMimetypes });
    }));
  }

  @Action(GetDocumentCategories)
  getDocumentCategories({ getState, patchState }: StateContext<DocumentStateModel>, {}: GetDocumentCategories) {
    // TODO anti-pattern: relying on async state to avoid api call
    // solution: rely on a synchronous value to check for previous action
    // TODO anti-pattern: combining seemingly related models into one state
    // solution: split separate models into separate states so each state can focus on managing the lifecycle of just one thing
    if (!getState().documentCategories.length) {
      return this.documentsService.getDocumentCategories().pipe(tap(documentCategories => {
        patchState({ documentCategories });
      }));
    }
  }

  @Action(SetDocumentContact)
  SetDocumentContact(
    ctx: StateContext<DocumentStateModel>,
    { documentId, contactId }: SetDocumentContact,
  ) {
    return this.documentsService.setDocumentContact(documentId, contactId)
      .pipe(
        catchError(err => {
          throw err;
        }),
        tap(response => {
          ctx.setState(patch({
            documents: updateItem( doc => doc.id === documentId, patch({
              ...response,
            })),
          }));
        })
      );
  }

  @Action(UpdateDocument)
  UpdateDocument(
    ctx: StateContext<DocumentStateModel>,
    { document }: UpdateDocument
  ) {
    ctx.patchState({loading: true});
    return this.documentsService.update(document).pipe(
      catchError((err) => {
        ctx.patchState({
          alert: {level: 'error', message: 'Failed to update document, please try again.'},
          loading: false,
        });
        throw err;
      }),
      tap(response => {
        ctx.setState(patch({
          loading: false,
          documents: updateItem( doc => doc.id === document.id, patch({
            ...response,
          })),
        }));
      })
    );
  }

  @Action(UpdateDocumentDrsVerification)
  updateDocumentDrsVerification({ patchState, getState }: StateContext<DocumentStateModel>, { document }: UpdateDocumentDrsVerification) {
    const documents = [...getState().documents];
    const updatedDocumentIndex = documents.findIndex(doc => doc.id === document.id);
    const oldDocument = documents[updatedDocumentIndex];
    const date = new Date();
    const mysqlDate = date.toISOString().split('T')[0] + ' ' + date.toTimeString().split(' ')[0];
    documents[updatedDocumentIndex] = {...document, verificationCreated: document.verificationCreated === null ? mysqlDate : null};
    patchState({ documents });
    return this.documentsService.updateDrsVerify(document).pipe(catchError(() => {
      const documentsCopy = [...getState().documents];
      documentsCopy[updatedDocumentIndex] = oldDocument;
      patchState({ documents: documentsCopy, alert: {
        level: 'error',
        message: 'Failed to update document, please try again.'
      }});
      return of();
    }));
  }

  @Action(DeleteDocument)
  deleteDocument({ getState, patchState }: StateContext<DocumentStateModel>, { documentId }: DeleteDocument) {
    return this.documentsService.delete(documentId).pipe(
      catchError(err => {
        patchState({
          alert: {
            level: 'error',
            message: 'Failed to delete document.'
          }
        });
        return of(null);
      }),
      tap(deletedDocumentId => {
        patchState({ documents: getState().documents.filter(document => document.id !== deletedDocumentId) });
    }));
  }

  @Action(UploadDocument)
  uploadDocument({ getState, patchState }: StateContext<DocumentStateModel>, {
    file,
    borrowerId,
    callbackFn,
  }: UploadDocument) {
    patchState({ isUploading: true });
    return this.documentsService.upload(file, borrowerId)
      .pipe(
        catchError(err => {
          let alertMessage = 'Failed to upload document, please try again.';
          let logMessage = '';
          let isExpectedError = false;

          if (err?.constructor.name === 'ProgressEvent') {
            logMessage = 'Failed uploading file via document.service.ts: Progress Event Error';
            isExpectedError = true;
          } else if (err?.data && err?.data['AWS Upload Error']) {
            logMessage = 'Failed uploading file via document.service.ts: AWS Upload Error';
            isExpectedError = true;
          } else if (err?.data && err?.data['Document validation errors']) {
            alertMessage = 'Failed to upload document: document validation errors.';
            isExpectedError = true;
          } else if (err?.data && err?.data['contents']) {
            alertMessage = 'Failed to upload document: document contents empty.';
            isExpectedError = true;
          }

          patchState({
            isUploading: false,
            alert: {
              level: 'error',
              message: alertMessage
            }
          });

          if (isExpectedError) {
            return of(null);
          }

          return throwError(() => {
            if (!logMessage && err) {
              return new Error(err);
            }

            return new Error(logMessage);
          });
        }),
        map(newDocument => {
          if (!newDocument) {
            return;
          }
          const documents = getState().documents;
          if (documents.findIndex(doc => doc.id === newDocument.id) === -1) {
            patchState({ documents: [newDocument, ...documents] });
          }

          if (callbackFn) {
            callbackFn(newDocument);
          }

          return newDocument;
        }),
        tap(() => {
          patchState({ isUploading: false });
        })
      );
  }

  @Action(UploadAdvDocument)
  uploadAdvDocument({ getState, patchState }: StateContext<DocumentStateModel>, {
    file,
    formData,
    callbackFn
  }: UploadAdvDocument) {
    patchState({ isUploading: true });
    return this.documentsService.advancedUpload(file, formData)
      .pipe(
        catchError(err => {
          let alertMessage = 'Failed to upload document, please try again.';
          let logMessage = '';
          let isExpectedError = false;

          if (err?.constructor.name === 'ProgressEvent') {
            logMessage = 'Failed uploading file via document.service.ts: Progress Event Error';
            isExpectedError = true;
          } else if (err?.data && err?.data['AWS Upload Error']) {
            logMessage = 'Failed uploading file via document.service.ts: AWS Upload Error';
            isExpectedError = true;
          } else if (err?.data && err?.data['Document validation errors']) {
            alertMessage = 'Failed to upload document: document validation errors.';
            isExpectedError = true;
          } else if (err?.data && err?.data['contents']) {
            alertMessage = 'Failed to upload document: document contents empty.';
            isExpectedError = true;
          }

          patchState({
            isUploading: false,
            alert: {
              level: 'error',
              message: alertMessage
            }
          });

          if (isExpectedError) {
            return of(null);
          }

          return throwError(() => {
            if (!logMessage && err) {
              return new Error(err);
            }

            return new Error(logMessage);
          });
        }),
        map(newDocument => {
          if (!newDocument) {
            return;
          }
          const documents = getState().documents;
          if (documents.findIndex(doc => doc.id === newDocument.id) === -1) {
            if (newDocument.contactId && (typeof newDocument.contactId === 'string')) {
              newDocument.contactId = parseInt(newDocument.contactId, 10);
            }
            patchState({ documents: [newDocument, ...documents] });
          }

          if (callbackFn) {
            callbackFn(newDocument);
          }

          return newDocument;
        }),
        tap(() => {
          patchState({ isUploading: false });
        })
      );
  }

  @Action(ClearDocumentStore)
  clearDocumentStore({ getState, patchState }: StateContext<DocumentStateModel>, { borrowerId }: ClearDocumentStore) {
    const prevBorrowerId = getState().borrowerId;
    if (prevBorrowerId) {
      this.pusherUnsubscribe(prevBorrowerId);
    }
    this.pusherUnsubscribe(borrowerId);
    patchState({
      borrowerId: null,
      documents: [],
      loading: true,
    });
  }

  @Action(RunExperianCommercial)
  runExperianCommercial({patchState}: StateContext<DocumentStateModel>, { borrowerId }: RunExperianCommercial) {
    return this.documentsService.runExperianCommercial(borrowerId).pipe(catchError(() => {
      patchState({
        alert: {
          level: 'error',
          message: 'Experian commercial credit pull failed.'
        }
      });
      return of([]);
    }),
    tap(() => {
      patchState({
        alert: {
          level: 'success',
          message: 'Success! Commercial credit pulled!'
        }
      });
    })
    );
  }

  pusherSubscribe(borrowerId: number) {
    this.pusherService
      .subscribe(`borrower-${borrowerId}-documents`)
      .bind_global((eventName: string) => {
        if (eventName.startsWith('document-')) {
          this.store.dispatch(new GetBorrowerDocuments(borrowerId, true));
        }
      });

    this.pusherService
      .subscribe(`borrower-${borrowerId}-ocrolus-activity`)
      .bind_global((eventName: string) => {
        if (eventName.startsWith('activity-')) {
          this.store.dispatch(new GetBorrowerDocuments(borrowerId, true));
        }
      });
  }

  pusherUnsubscribe(borrowerId: number) {
    this.pusherService.unsubscribe(`borrower-${borrowerId}-documents`);
    this.pusherService.unsubscribe(`borrower-${borrowerId}-ocrolus-activity`);
  }
}
